import React, { useState } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormGroup } from "reactstrap"
import { useMutation, gql, useQuery } from "@apollo/client"
import "./CommsNewConversationModal.css"
import { GET_INBOX_CHANNELS } from "./Queries"
import { DriverTypeAhead } from "../TaskManagement/TaskComponents/TypeAheadComponents"
import { useNavigate } from "react-router-dom"

const UPSERT_CONVERSATION_MESSAGE = gql`
  mutation MessagingUpsertConversationMessage(
    $to: String
    $driverId: ID
    $body: String!
    $channelId: ID
    $inboxId: ID
    $subject: String
    $conversationId: ID
  ) {
    messagingUpsertConversationMessage(
      to: $to
      driverId: $driverId
      body: $body
      channelId: $channelId
      inboxId: $inboxId
      subject: $subject
      conversationId: $conversationId
    ) {
      ok
      errors
      conversation {
        id
      }
      message {
        id
      }
    }
  }
`

export default function CommsNewConversationModal({ isOpen, toggle, allInboxes, currentUser }) {
  // Usenavigate
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    inboxId: "",
    to: "",
    driverId: "",
    subject: "",
    body: "",
    channelId: "",
  })
  const [error, setError] = useState('')

  const [sendMutation, { loading, error: mutationError }] = useMutation(UPSERT_CONVERSATION_MESSAGE)

  const { data: channelsData, loading: loadingChannels, error: errorChannels } = useQuery(
    GET_INBOX_CHANNELS, {
    variables: { inboxId: formData.inboxId, first: 50 },
    skip: !formData.inboxId,
    fetchPolicy: 'cache-and-network',
  })
  const channels = channelsData?.allChannels?.edges.map(edge => edge.node) || []

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSend = async () => {
    try {
      const variables = {
        to: formData.to,
        driverId: formData.driverId || null,
        body: formData.body,
        subject: formData.subject,
        inboxId: formData.inboxId || null,
        channelId: formData.channelId || null,
      }

      const { data } = await sendMutation({ variables })
      if (data?.messagingUpsertConversationMessage?.ok) {
        toggle()
        // Redirect to the new conversation could be handled here
        const newConversationId = data?.messagingUpsertConversationMessage?.conversation?.id
        navigate(`/comms/conversation/${newConversationId}/`)

        setFormData({
          inboxId: "",
          driverId: "",
          to: "",
          subject: "",
          body: "",
          channelId: "",
        })
        setError('')
      } else {
        setError(data?.messagingUpsertConversationMessage?.errors?.toString() || "An unknown error occurred")
        console.error("GraphQL error:", data?.messagingUpsertConversationMessage?.errors)
      }
    } catch (err) {
      setError(err.toString())
      console.error("Send error:", err)
    }
  }

  return (
    <Modal isOpen={isOpen} centered size="md" className="CommsNewConversationModal">
      <ModalHeader>Start a New Conversation</ModalHeader>
      <ModalBody>
        {error && <div className="alert alert-danger">Error: {error}</div>}

        <FormGroup>
          <Label for="inboxId">Inbox</Label>
          <Input
            type="select"
            name="inboxId"
            value={formData.inboxId}
            onChange={(e) => {
              handleChange(e)
              // Reset channelId when inbox changes
              setFormData(prev => ({ ...prev, channelId: "" }))
            }}
          >
            <option value="">Select an inbox</option>
            {allInboxes.map((inbox, idx) => (
              <option key={idx} value={inbox.id}>{inbox.name}</option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup>
          <Label for="channelId">Channel</Label>
          <Input
            type="select"
            name="channelId"
            value={formData.channelId}
            disabled={!formData.inboxId || loadingChannels || errorChannels}
            onChange={handleChange}
          >
            <option value="">Select a channel</option>
            {channels.map((channel, idx) => (
              <option key={idx} value={channel.id}>{channel.name}</option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup>
          <Label for="to">Recipient</Label>
          <DriverTypeAhead onChange={(driverId) => setFormData(prev => ({ ...prev, driverId }))} />
        </FormGroup>

        <FormGroup>
          <Label for="subject">Subject</Label>
          <Input
            type="text"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
          />
        </FormGroup>

        <FormGroup>
          <Label for="body">Message</Label>
          <Input
            type="textarea"
            name="body"
            rows="4"
            value={formData.body}
            onChange={handleChange}
            placeholder="Type your message..."
          />
        </FormGroup>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>Cancel</Button>
        <Button
          color="primary"
          onClick={handleSend}
          disabled={loading || !formData.body || (!formData.to && !formData.channelId)}
        >
          {loading ? "Sending..." : "Send"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
